/**
 * Which expiry is the current week, the next week, the current month — 9.24.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DERIVED FROM THE CATALOGUE, NEVER FROM A CALENDAR
 *
 * The temptation is to compute "this week's expiry" from today's date: find
 * Tuesday, done. ROADMAP 5.17 already records what that costs — the reference
 * engine hardcoded Tuesday weeklies and last-Thursday monthlies, those two
 * rules disagree with each other, and NSE has moved expiry days more than
 * once. A guessed expiry resolves to a contract that does not exist, or
 * worse, to one that does and is the wrong week.
 *
 * So the series is positional over what the broker actually lists:
 *
 *   CW   the nearest expiry that is not its month's last
 *   NW   the one after that
 *   CM   the nearest expiry that IS its month's last
 *   NM   the one after that
 *
 * `monthly` comes from `buildExpiries`, which marks the last expiry in each
 * calendar month and corrects itself as the later ones are listed.
 *
 * AN EXPIRY CAN BE BOTH, AND MONTHLY WINS. In the last week of a month the
 * nearest expiry is also that month's last, so it is CM and there is no CW
 * at all. That is not a gap to paper over — in that week there IS no weekly
 * before the monthly, and labelling the monthly "CW" would say there was.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { ExpiryItem } from './expiryModel'

/** The short tag a chip wears, or null for an expiry further out. */
export type ExpirySeries = 'CW' | 'NW' | 'CM' | 'NM'

export const SERIES_TITLE: Record<ExpirySeries, string> = {
  CW: 'Current week',
  NW: 'Next week',
  CM: 'Current month',
  NM: 'Next month',
}

/**
 * Tag each expiry, in the order given.
 *
 * Returns a map rather than mutating the items: `ExpiryItem` is shared with
 * the picker and the catalogue, and a field only this strip reads does not
 * belong on it.
 */
export function seriesOf(items: readonly ExpiryItem[]): Map<string, ExpirySeries> {
  const out = new Map<string, ExpirySeries>()
  const weekly: string[] = []
  const monthly: string[] = []

  for (const item of items) {
    // Nearest first, which is the order `buildExpiries` already returns.
    ;(item.monthly ? monthly : weekly).push(item.iso)
  }

  if (weekly[0]) out.set(weekly[0], 'CW')
  if (weekly[1]) out.set(weekly[1], 'NW')
  if (monthly[0]) out.set(monthly[0], 'CM')
  if (monthly[1]) out.set(monthly[1], 'NM')
  return out
}

/** "9 NOV '26" — the chip's own line, upper case because the strip is dense. */
export function chipDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return iso
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(d.getTime())) return iso
  const month = d.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()
  return `${d.getDate()} ${month} '${String(d.getFullYear()).slice(2)}`
}

/**
 * The second line: "CW: 5 DTE", or just "27 DTE" past the fourth.
 *
 * The number is there for every chip because days-to-expiry is the thing
 * actually being compared; the tag is there for the four that have a name.
 */
export function chipSub(dte: number, series: ExpirySeries | undefined): string {
  return series ? `${series}: ${dte} DTE` : `${dte} DTE`
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW FAR OUT THE STRIP LISTS — ROADMAP 9.26
 *
 * A broker's master file carries every listed contract, and for an index
 * that runs to the far quarterly and annual series: NIFTY lists expiries
 * into 2031. Those are real contracts and the chain can price them, but on
 * a strip they are 8 chips of 1,500-DTE noise after the 6 anyone trades,
 * and they are what pushes the near series off the left edge.
 *
 * THE HORIZON IS THE END OF NEXT CALENDAR YEAR. In November 2026 the strip
 * stops at 31 December 2027.
 *
 * A fixed boundary rather than a rolling window ("today plus twelve
 * months"), because a rolling one makes the LAST chip move every month: the
 * December annual is listed in November, gone in January, back in February.
 * The year-end boundary moves once a year and takes the whole next year's
 * series with it when it does.
 *
 * DERIVED, NEVER HARDCODED. The cut-off is computed from the clock, so no
 * build of this ever ships a date that expires. And it is applied to what
 * the BROKER lists: an instrument that lists nothing beyond next March
 * shows those and stops, which is the same rule producing a shorter answer.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The last date the strip will list, as an ISO day. */
export function horizonEnd(now: Date = new Date()): string {
  return `${now.getFullYear() + 1}-12-31`
}

/**
 * Drop the expiries past the horizon.
 *
 * `keep` is the CHOSEN expiry, and it survives the cut whatever its date.
 * A chain restored from storage, or one a strategy named, can sit beyond
 * the horizon; dropping its chip would leave the strip with nothing marked
 * while the ladder below it shows that very contract.
 *
 * ISO days compare correctly as strings — `YYYY-MM-DD` is ordered the same
 * either way — so this needs no date parsing and cannot drift with a
 * timezone the way `new Date(iso) <= cutoff` would at the boundary.
 */
export function withinHorizon(
  items: readonly ExpiryItem[],
  now: Date = new Date(),
  keep: string | null = null,
): ExpiryItem[] {
  const end = horizonEnd(now)
  return items.filter((item) => item.iso <= end || item.iso === keep)
}
